// src/components/resumo/annotations/HighlightCommentIcon.tsx
// 🎯 Ícone de comentário que aparece ao clicar em grifo

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Save, X } from 'lucide-react';
import { useLayoutEffect } from 'react';

interface HighlightCommentIconProps {
    highlightId: string;
    existingComment?: string;
    onSave: (comment: string) => void;
    onClose: () => void;
    darkMode?: boolean;
}

const HighlightCommentIcon: React.FC<HighlightCommentIconProps> = ({
    highlightId,
    existingComment = '',
    onSave,
    onClose,
    darkMode = false
}) => {
    const [showInput, setShowInput] = useState(false);
    const [comment, setComment] = useState(existingComment);
    const [position, setPosition] = useState<{
        iconTop: number;
        iconLeft: number;
    } | null>(null);
    const [boxStyle, setBoxStyle] = useState<{
        top: number;
        left: number;
        maxWidth: number;
    } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const mark = document.querySelector(`[data-highlight-id="${highlightId}"]`);
        if (!mark) return;

        const rect = mark.getBoundingClientRect();

        const iconSize = 36;
        let iconTop = rect.top - iconSize - 8;
        let iconLeft = rect.left + (rect.width / 2) - (iconSize / 2);

        if (iconLeft < 8) {
            iconLeft = 8;
        }

        if (iconLeft + iconSize > window.innerWidth - 8) {
            iconLeft = window.innerWidth - iconSize - 8;
        }

        const containerHeight = 150;
        if (iconTop + containerHeight > window.innerHeight) {
            iconTop = rect.bottom + 8;
        }

        setPosition({ iconTop, iconLeft });
    }, [highlightId]);

    useLayoutEffect(() => {
        if (!showInput || !boxRef.current || !position) return;

        const box = boxRef.current;
        const padding = 16;
        const boxWidth = box.offsetWidth;
        const boxHeight = box.offsetHeight;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = position.iconLeft - (boxWidth / 2) + 18;
        let top = position.iconTop - boxHeight + 20;

        if (top < padding) {
            top = position.iconTop + 28;
        }

        if (left + boxWidth > viewportWidth - padding) {
            left = viewportWidth - boxWidth - padding;
        }

        if (left < padding) {
            left = padding;
        }

        if (top + boxHeight > viewportHeight - padding) {
            top = position.iconTop - boxHeight - 12;
        }

        const availableRight = viewportWidth - left - padding;
        const maxWidth = Math.min(300, availableRight);

        setBoxStyle({ top, left, maxWidth });
    }, [showInput, position]);

    useEffect(() => {
        if (showInput && inputRef.current) {
            inputRef.current.focus();
        }
    }, [showInput]);

    const handleSave = useCallback(() => {
        if (comment.trim()) {
            onSave(comment.trim());
        }
        setShowInput(false);
    }, [comment, onSave]);

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (iconRef.current && !iconRef.current.contains(e.target as Node)) {
            if (showInput && comment.trim()) {
                onSave(comment.trim());
                setShowInput(false);
            } else {
                onClose();
            }
        }
    }, [showInput, comment, onSave, onClose]);

    useEffect(() => {
        if (!showInput) return;

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showInput, handleClickOutside]);

    if (!position) return null;

    return (
        <div
            ref={iconRef}
            className={`highlight-comment-icon ${darkMode ? 'dark' : ''}`}
            style={{
                position: 'fixed',
                top: position.iconTop,
                left: position.iconLeft,
                zIndex: 1002
            }}
        >
            {!showInput ? (
                <button
                    className="comment-icon-btn"
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        console.log('🔵 Abrindo input de comentário');
                        setShowInput(true);
                    }}
                    title={existingComment ? 'Ver comentário' : 'Adicionar comentário'}
                >
                    <MessageSquare size={18} />
                    {existingComment && <span className="comment-indicator">•</span>}
                </button>
            ) : (
                <div
                    ref={boxRef}
                    className="comment-input-container"
                    style={{
                        position: 'fixed',
                        top: boxStyle?.top,
                        left: boxStyle?.left,
                        maxWidth: boxStyle?.maxWidth,
                        zIndex: 1003
                    }}
                >
                    <textarea
                        ref={inputRef}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Digite seu comentário..."
                        rows={3}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey) {
                                handleSave();
                            } else if (e.key === 'Escape') {
                                setShowInput(false);
                            }
                        }}
                    />
                    <div className="comment-actions">
                        <button
                            className="comment-action-btn cancel"
                            onClick={() => setShowInput(false)}
                        >
                            <X size={14} />
                        </button>
                        <button
                            className="comment-action-btn save"
                            onClick={handleSave}
                        >
                            <Save size={14} />
                        </button>
                    </div>
                    <div className="comment-hint">Ctrl+Enter para salvar</div>
                </div>
            )}
        </div>
    );
};

export default HighlightCommentIcon;